/**
 * 配件库存管理页
 */
import React, { useState, useEffect } from 'react';
import { Table, Tag, Input, Button, Card, Row, Col, Modal, Form, InputNumber, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, SearchOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { partsApi } from '../../services/api';

function PartsList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ keyword: '', supplier: '', stockAlert: '' });
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const columns = [
    { title: '产品名称', dataIndex: 'product_name', width: 160 },
    { title: '型号规格', dataIndex: 'model_spec', width: 140, render: (v) => v || '-' },
    { title: 'SN/序列号', dataIndex: 'sn', width: 140, render: (v) => v || '-' },
    {
      title: '库存数量', dataIndex: 'quantity', width: 100, sorter: (a, b) => a.quantity - b.quantity,
      render: (v, record) => (
        <Space>
          <span style={{ fontWeight: 'bold', color: v <= record.min_stock ? '#ff4d4f' : '#52c41a' }}>{v}</span>
          {v <= record.min_stock && <WarningOutlined style={{ color: '#ff4d4f' }} />}
        </Space>
      ),
    },
    { title: '单位', dataIndex: 'unit', width: 60 },
    { title: '预警值', dataIndex: 'min_stock', width: 70 },
    { title: '单价', dataIndex: 'unit_price', width: 90, render: (v) => `¥${parseFloat(v).toFixed(2)}` },
    { title: '总价值', dataIndex: 'total_value', width: 100, render: (v) => `¥${parseFloat(v).toFixed(2)}` },
    { title: '供应商', dataIndex: 'supplier', width: 120, render: (v) => v || '-' },
    { title: '存货地址', dataIndex: 'storage_location', width: 150, ellipsis: true, render: (v) => v || '-' },
    {
      title: '操作', width: 120, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除该配件？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const loadData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (filters.keyword) params.keyword = filters.keyword;
      if (filters.supplier) params.supplier = filters.supplier;
      if (filters.stockAlert) params.stockAlert = 'true';
      const res = await partsApi.list(params);
      setData(res.data?.list || []);
      setPagination({ page: res.data?.pagination?.page || 1, pageSize: res.data?.pagination?.pageSize || 20, total: res.data?.pagination?.total || 0 });
    } catch (err) { } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const openCreate = () => { setEditing(null); form.resetFields(); setModal(true); };
  const openEdit = (record) => { setEditing(record); form.setFieldsValue(record); setModal(true); };

  const handleSubmit = async (values) => {
    try {
      if (editing) {
        await partsApi.update(editing.id, values);
        message.success('配件更新成功');
      } else {
        await partsApi.create(values);
        message.success('配件添加成功');
      }
      setModal(false);
      loadData(pagination.page, pagination.pageSize);
    } catch (err) { }
  };

  const handleDelete = async (id) => {
    try { await partsApi.remove(id); message.success('删除成功'); loadData(pagination.page, pagination.pageSize); } catch (err) { }
  };

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col><Input placeholder="搜索名称/SN/型号" prefix={<SearchOutlined />} value={filters.keyword}
            onChange={e => setFilters({ ...filters, keyword: e.target.value })} onPressEnter={() => loadData()} style={{ width: 220 }} allowClear /></Col>
          <Col><Input placeholder="供应商" value={filters.supplier}
            onChange={e => setFilters({ ...filters, supplier: e.target.value })} onPressEnter={() => loadData()} style={{ width: 150 }} allowClear /></Col>
          <Col>
            <Button type={filters.stockAlert ? 'primary' : 'default'} danger={filters.stockAlert}
              onClick={() => { setFilters({ ...filters, stockAlert: filters.stockAlert ? '' : 'true' }); setTimeout(() => loadData(), 0); }}>
              <WarningOutlined /> 低库存
            </Button>
          </Col>
          <Col><Button type="primary" icon={<SearchOutlined />} onClick={() => loadData()}>搜索</Button></Col>
          <Col><Button icon={<ReloadOutlined />} onClick={() => { setFilters({ keyword: '', supplier: '', stockAlert: '' }); setTimeout(() => loadData(), 0); }}>重置</Button></Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>添加配件</Button>
          </Col>
        </Row>
      </Card>

      <Table rowKey="id" columns={columns} dataSource={data} loading={loading}
        pagination={{ ...pagination, showSizeChanger: true, showTotal: (t) => `共 ${t} 条`, onChange: (page, pageSize) => loadData(page, pageSize) }}
        scroll={{ x: 1400 }} />

      <Modal title={editing ? '编辑配件' : '添加配件'} open={modal} onCancel={() => setModal(false)} onOk={() => form.submit()} width={600}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="product_name" label="产品名称" rules={[{ required: true, message: '请输入产品名称' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="model_spec" label="型号规格">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="quantity" label="数量" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="unit" label="单位" initialValue="个">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="min_stock" label="最低库存预警" initialValue={5}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="unit_price" label="单价 (元)">
                <InputNumber min={0} step={0.01} prefix="¥" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sn" label="序列号/SN">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="supplier" label="供应商">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="storage_location" label="存货地址/库位">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default PartsList;
